import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, Copy, KeyRound, ShieldAlert, TriangleAlert } from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import { useSetupStatus } from "@/lib/auth";
import { copyText } from "@/lib/clipboard";

interface SetupResult {
  userId: string;
  orgId: string;
  orgSlug: string;
  masterKey: string;
  redirect: string;
}

export function Setup() {
  const setup = useSetupStatus();
  const qc = useQueryClient();
  const nav = useNavigate();

  const [step, setStep] = useState<1 | 2>(1);
  const [orgName, setOrgName] = useState("");
  const [orgSlug, setOrgSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [result, setResult] = useState<SetupResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "ok" | "failed">("idle");
  const copied = copyState === "ok";

  async function doCopy(text: string) {
    const ok = await copyText(text);
    setCopyState(ok ? "ok" : "failed");
  }

  function slugify(s: string): string {
    return s.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/-+/g, "-").replace(/^-+|-+$/g, "");
  }
  function onOrgNameChange(v: string) {
    setOrgName(v);
    if (!slugEdited) setOrgSlug(slugify(v));
  }

  const m = useMutation({
    mutationFn: () =>
      api.post<SetupResult>("/setup", {
        email,
        password,
        // Platform apex + Cloudflare config moved to Settings (post-setup);
        // first-install only needs admin creds, an org, and a master key.
        orgSlug: orgSlug || slugify(orgName) || "default",
        orgName: orgName || "Default",
      }),
  });

  if (setup.data && setup.data.seeded && !result) return <Navigate to="/login" replace />;

  const validSlug = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(orgSlug);
  const canSubmit = !!email && password.length >= 8 && orgName.trim().length > 0 && validSlug;

  async function onAdminSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setErr(null);
    try {
      const r = await m.mutateAsync();
      setResult(r);
      setStep(2);
      qc.invalidateQueries({ queryKey: ["setup-status"] });
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : "Setup failed");
    }
  }

  function onContinue(e: React.FormEvent) {
    e.preventDefault();
    if (!copied) return;
    nav("/login");
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 bg-subtle/50">
      <div className="w-full max-w-xl">
        <div className="mb-6">
          <h1 className="text-3xl font-display italic">
            Welcome to <span className="text-accent">openfront</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            One-time setup. After you finish, this page is permanently disabled.
            Domain + TLS configuration happens later in Settings.
          </p>
        </div>

        <ol className="flex items-center gap-2 mb-6 text-xs">
          {[1, 2].map((n, i) => (
            <li key={n} className="flex items-center gap-2">
              <span className={
                "h-6 w-6 rounded-full grid place-items-center font-semibold text-2xs " +
                (n < step ? "bg-success-emphasis text-white" :
                 n === step ? "bg-accent-emphasis text-white" :
                 "bg-subtle text-subtle-foreground border border-border")
              }>
                {n < step ? <Check className="h-3.5 w-3.5" /> : n}
              </span>
              <span className={n === step ? "font-semibold text-foreground" : "text-muted-foreground"}>
                {n === 1 ? "Organization" : "Master key"}
              </span>
              {i < 1 && <span className="text-subtle-foreground">→</span>}
            </li>
          ))}
        </ol>

        <div className="of-card p-6">
          {step === 1 && (
            <form onSubmit={onAdminSubmit} className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold">Create your organization &amp; admin</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  There is no public sign-up. This admin is the only way to invite teammates later.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="org-name">Organization name</Label>
                  <Input
                    id="org-name"
                    autoFocus
                    required
                    value={orgName}
                    onChange={(e) => onOrgNameChange(e.target.value)}
                    placeholder="Acme Inc"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="org-slug">URL slug</Label>
                  <Input
                    id="org-slug"
                    required
                    pattern="[a-z0-9]([a-z0-9-]*[a-z0-9])?"
                    value={orgSlug}
                    onChange={(e) => { setOrgSlug(e.target.value.toLowerCase()); setSlugEdited(true); }}
                    placeholder="acme"
                  />
                  {orgSlug && !validSlug && (
                    <p className="text-2xs text-danger">
                      Lowercase letters, digits, hyphens only. Must start &amp; end with a letter/digit.
                    </p>
                  )}
                  {validSlug && (
                    <p className="text-2xs text-subtle-foreground font-mono">
                      / orgs / {orgSlug}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">Admin email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password (≥ 8 characters)</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {err && (
                <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">
                  {err}
                </div>
              )}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!canSubmit || m.isPending}
                >
                  {m.isPending ? "Creating…" : <>Create &amp; continue <ArrowRight className="h-3.5 w-3.5" /></>}
                </Button>
              </div>
            </form>
          )}

          {step === 2 && result && (
            <form onSubmit={onContinue} className="space-y-5">
              <div className="flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-attention shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-lg font-semibold">Save your master encryption key</h2>
                  <p className="text-sm text-muted-foreground">
                    This key is shown <strong>once</strong>. It is excluded from backups by design.
                    Lose it and every encrypted env var becomes unrecoverable.
                  </p>
                </div>
              </div>
              <textarea
                readOnly
                value={result.masterKey}
                onFocus={(e) => e.currentTarget.select()}
                className="of-mono-surface rounded-md border border-border p-3 font-mono text-xs break-all w-full h-20 resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
                aria-label="Master encryption key"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant={copied ? "primary" : "default"}
                  onClick={() => doCopy(result.masterKey)}
                >
                  {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy to clipboard</>}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setCopyState("ok"); }}
                  title="I have copied this manually"
                >
                  Mark as copied
                </Button>
              </div>
              {copyState === "failed" && (
                <div className="flex items-start gap-2 text-xs text-attention bg-attention-subtle border border-attention/30 rounded-md px-2.5 py-1.5">
                  <TriangleAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <div>
                    Browser blocked clipboard access (this happens on plain
                    <span className="font-mono"> http://</span> origins). Select
                    the field above and copy with <kbd className="font-mono bg-subtle px-1 rounded">⌘C</kbd> /
                    <kbd className="font-mono bg-subtle px-1 rounded">Ctrl+C</kbd>, then click
                    <strong> Mark as copied</strong>.
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 text-2xs text-subtle-foreground">
                <KeyRound className="h-3 w-3" />
                Store this in a password manager outside the platform.
              </div>
              <div className="flex justify-end pt-2">
                <Button type="submit" variant="primary" disabled={!copied}>
                  Continue to sign in <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </form>
          )}
        </div>

        <p className="text-2xs text-subtle-foreground text-center mt-4">
          After setup: Settings → Platform domain + Wildcard certificate.
        </p>
      </div>
    </div>
  );
}
